import type { AccessorFn } from "@tanstack/react-table";
import type { RecordColumnDef } from "@/components/ui/record-table/column-types";
import {
  createCurrencyColumn,
  createNumberColumn,
} from "@/components/ui/record-table/column-factories/data-columns";
import {
  createPrimaryColumn,
  createTextColumn,
} from "@/components/ui/record-table/column-factories/text-columns";
import type {
  AccessoryColumnDefinition,
  AccessoryProduct,
} from "@/lib/constants/accessory-columns";

function numberAccessor(
  accessor: AccessorFn<AccessoryProduct, unknown>,
): (product: AccessoryProduct) => number | undefined {
  return (product) => {
    const value = accessor(product, 0);
    return typeof value === "number" ? value : undefined;
  };
}

function textAccessor(
  accessor: AccessorFn<AccessoryProduct, unknown>,
): (product: AccessoryProduct) => string | undefined {
  return (product) => {
    const value = accessor(product, 0);
    return value === null || value === undefined ? undefined : String(value);
  };
}

export function buildAccessoryColumns(
  definitions: AccessoryColumnDefinition[],
  getHref: (product: AccessoryProduct) => string,
): RecordColumnDef<AccessoryProduct, unknown>[] {
  return definitions.map((definition) => {
    const base = {
      header: definition.header,
      size: definition.width,
      enableSorting: definition.sortable ?? true,
    };

    if (definition.format === "price") {
      return createCurrencyColumn<AccessoryProduct>({
        ...base,
        accessor: numberAccessor(definition.accessor),
      });
    }

    if (definition.format === "quantity") {
      return createNumberColumn<AccessoryProduct>({
        ...base,
        accessor: numberAccessor(definition.accessor),
      });
    }

    if (definition.id === "title") {
      return createPrimaryColumn<AccessoryProduct>({
        ...base,
        accessor: textAccessor(definition.accessor),
        getHref,
      });
    }

    return createTextColumn<AccessoryProduct>({
      ...base,
      accessor: textAccessor(definition.accessor),
    });
  });
}
